import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import { PlaceHolderHouseUrl } from '../../components/Svg'
import { Arrows } from './Arrows'

const MAX_DOTS = 5
const PLACEHOLDER = PlaceHolderHouseUrl

export const PhotoGalleryCarousel = ({ photos, initialIndex, setLoading }) => {
  const hasImages = photos && photos.length > 0
  const galleryImages = hasImages ? photos : [PLACEHOLDER]

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [isAnimating, setIsAnimating] = useState(false)

  const goTo = useCallback(index => {
    if (isAnimating) {return}
    setIsAnimating(true)
    setCurrentIndex((index + galleryImages.length) % galleryImages.length)
  }, [isAnimating, setIsAnimating, setCurrentIndex, galleryImages])

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 300)
    return () => clearTimeout(timer)
  }, [currentIndex])

  const handleNext = useCallback((e) => {
    e.preventDefault()
    goTo(currentIndex + 1)
  }, [goTo, currentIndex])

  const handlePrev = useCallback((e) => {
    e.preventDefault()
    goTo(currentIndex - 1)
  }, [goTo, currentIndex])

  const getDotRange = useCallback(() => {
    const total = galleryImages.length
    if (total <= MAX_DOTS) {return [...Array(total).keys()]}

    const half = Math.floor(MAX_DOTS / 2)
    let start = Math.max(0, currentIndex - half)
    let end = start + MAX_DOTS

    if (end > total) {
      end = total
      start = total - MAX_DOTS
    }

    return Array.from({ length: MAX_DOTS }, (_, i) => start + i)
  }, [galleryImages, currentIndex])

  return (
    <div
      className='PhotoCarousel'
    >
      <div className='PhotoCarousel-imageContainer'>
        <div
            className='PhotoCarousel-imageAnimationContainer'
            style={{
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: isAnimating ? 'transform 0.3s ease' : 'none'
            }}
        >
            {galleryImages.map((image, i) => {
              return (
                <div
                  key={image.id}
                  className='PhotoCarousel-imageWrapper'
                >
                    <img
                      src={image?.webp ?? PlaceHolderHouseUrl}
                      alt={`Slide ${i + 1}`}
                      loading={(i === 1 || i === galleryImages.length - 1) ? 'eager' : 'lazy'}
                      onLoad={() => {
                        if (i === 1 || i === galleryImages.length - 1) {
                          setLoading(false)
                        }}
                      }
                      className='PhotoCarousel-image'
                      data-sprinkle='replace-broken-image'
                      data-replace-broken-image-url={PlaceHolderHouseUrl}
                    />
                </div>
              )
            })}
        </div>

        {(hasImages) && (<Arrows onNextClick={handleNext} onPreviusClick={handlePrev} />)}
      </div>

      {hasImages && (
        <div className='PhotoCarousel-dots'>
          <div className='PhotoCarousel-dotsWrapper'>
            {getDotRange().map(i => (
              <span
                key={i}
                className={cn('PhotoCarousel-dot', { 'PhotoCarousel-dot--activeDot': i === currentIndex })}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

PhotoGalleryCarousel.propTypes = {
  photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        jpeg: PropTypes.string.isRequired,
        webp: PropTypes.string.isRequired
      })
    )
}

